import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'
import { EI_INPUT_FIELD_CONSTANTS } from '@app/shared/constants/ei-input-field.constants'
import {
    SECTION_OTHER_ALLOWED_KEYS,
    USER_FIELD_MAX_LENGTH,
    USER_FIELD_VALIDATION,
} from '../constants/id-creation.constants'
import type {
    IInputFieldConfig,
    TInputFieldTextFilter,
    TInputFieldValue,
} from '../shared/types/input-field.types'
import { formatLocalDate } from '@/styles/components/calendar/calendar.utils'

export function sanitizeTextByFilter(value: string, filter: TInputFieldTextFilter): string {
    switch (filter) {
        case 'lettersOnly':
            return value.replace(REGEX_PATTERNS.NON_LETTER_CHARS_GLOBAL, '')
        case 'namePart':
            return value.replace(REGEX_PATTERNS.NON_NAME_PART_CHARS_GLOBAL, '')
        case 'digitsOnly':
            return value.replace(REGEX_PATTERNS.NON_NUMERIC, '')
        case 'section':
            return value.replace(REGEX_PATTERNS.NON_SECTION_CHARS_GLOBAL, '')
        case 'alphanumericOnly':
            return value.replace(REGEX_PATTERNS.NON_ALPHANUMERIC_CHARS_GLOBAL, '')
    }
}

export function sanitizePersonName(value: string): string {
    return sanitizeTextByFilter(value, 'namePart')
}

export function sanitizeNamePart(value: string): string {
    return sanitizeTextByFilter(value, 'namePart')
}

export function sanitizeRollNumber(value: string): string {
    return sanitizeTextByFilter(value, 'digitsOnly')
}

export function sanitizeEnrollmentNumber(value: string): string {
    return sanitizeTextByFilter(value, 'alphanumericOnly')
}

export function sanitizeSection(value: string): string {
    return sanitizeTextByFilter(value, 'section')
}

export function isValidUserNamePart(value: string): boolean {
    return REGEX_PATTERNS.USER_NAME_PART.test(value)
}

export function isValidSectionValue(value: string): boolean {
    return REGEX_PATTERNS.USER_SECTION.test(value)
}

export function isValidRollNumberValue(value: string): boolean {
    const trimmed = value.trim()
    if (!trimmed) {
        return true
    }
    if (!REGEX_PATTERNS.USER_ROLL_NUMBER.test(trimmed)) {
        return false
    }
    return parseInt(trimmed, 10) !== 0
}

export function isValidEnrollmentNumberValue(value: string): boolean {
    return REGEX_PATTERNS.USER_ENROLLMENT_NUMBER.test(value)
}

export function getNamePartValidationError(fieldLabel: string, value: string): string | null {
    const trimmed = value.trim()
    if (!trimmed) {
        return null
    }
    if (!isValidUserNamePart(trimmed)) {
        return USER_FIELD_VALIDATION.messages.nameInvalidChars(fieldLabel)
    }
    if (trimmed.length > USER_FIELD_MAX_LENGTH.NAME) {
        return USER_FIELD_VALIDATION.messages.maxLength(fieldLabel, USER_FIELD_MAX_LENGTH.NAME)
    }
    return null
}

export function getSectionValidationError(value: string): string | null {
    const trimmed = value.trim()
    if (!trimmed) {
        return null
    }
    const sanitized = sanitizeSection(trimmed)
    if (sanitized !== trimmed) {
        return USER_FIELD_VALIDATION.messages.sectionInvalidChars
    }
    if (sanitized.length > USER_FIELD_MAX_LENGTH.SECTION) {
        return USER_FIELD_VALIDATION.messages.maxLength('Section', USER_FIELD_MAX_LENGTH.SECTION)
    }
    return null
}

export function getRollNumberValidationError(value: string): string | null {
    const trimmed = value.trim()
    if (!trimmed) {
        return null
    }
    if (!REGEX_PATTERNS.USER_ROLL_NUMBER.test(trimmed)) {
        return USER_FIELD_VALIDATION.messages.rollInvalidChars
    }
    if (parseInt(trimmed, 10) === 0) {
        return USER_FIELD_VALIDATION.messages.rollCannotBeZero
    }
    if (trimmed.length > USER_FIELD_MAX_LENGTH.ROLL_NUMBER) {
        return USER_FIELD_VALIDATION.messages.maxLength(
            'Roll Number',
            USER_FIELD_MAX_LENGTH.ROLL_NUMBER,
        )
    }
    return null
}

export function getEnrollmentNumberValidationError(value: string): string | null {
    const trimmed = value.trim()
    if (!trimmed) {
        return null
    }
    if (!isValidEnrollmentNumberValue(trimmed)) {
        return USER_FIELD_VALIDATION.messages.enrollmentInvalidChars
    }
    if (trimmed.length > USER_FIELD_MAX_LENGTH.ENROLLMENT_NUMBER) {
        return USER_FIELD_VALIDATION.messages.maxLength(
            'Enrollment Number',
            USER_FIELD_MAX_LENGTH.ENROLLMENT_NUMBER,
        )
    }
    return null
}

export function getTitleOrDesignationValidationError(
    fieldLabel: string,
    value: string,
    maxLength: number,
): string | null {
    const trimmed = value.trim()
    if (!trimmed) {
        return null
    }
    if (!isValidUserNamePart(trimmed)) {
        return USER_FIELD_VALIDATION.messages.nameInvalidChars(fieldLabel)
    }
    if (trimmed.length > maxLength) {
        return USER_FIELD_VALIDATION.messages.maxLength(fieldLabel, maxLength)
    }
    return null
}

/**
 * Convert an input field value to a trimmed string (dates formatted as YYYY-MM-DD).
 */
export function inputFieldValueToString(value: TInputFieldValue): string {
    if (value == null) return ''
    if (typeof value === 'string') return value.trim()
    if (value instanceof Date) return formatLocalDate(value)
    return String(value).trim()
}

/**
 * Convert an input field value to a date string YYYY-MM-DD (for API payloads).
 */
export function inputFieldValueToDateString(value: TInputFieldValue): string {
    if (value == null) return ''
    if (value instanceof Date) return formatLocalDate(value)
    if (typeof value === 'string') return value.trim().slice(0, 10)
    return ''
}

/**
 * Keeps the native input value in sync with `sanitizeSection` so invalid characters
 * cannot remain visible (Angular `[value]` + signal alone does not always update the DOM).
 */
export function syncSectionOtherInputFromEvent(event: Event): {
    value: string
    hadInvalid: boolean
} {
    const target = event.target as HTMLInputElement
    const raw = target.value
    const sanitized = sanitizeSection(raw)
    const hadInvalid = sanitized !== raw
    if (hadInvalid) {
        const caret = target.selectionStart ?? raw.length
        const beforeSan = sanitizeSection(raw.slice(0, caret))
        target.value = sanitized
        const pos = Math.min(beforeSan.length, sanitized.length)
        setTimeout(() => target.setSelectionRange(pos, pos), 0)
    }
    return { value: sanitized, hadInvalid }
}

/**
 * Blocks keys that are not allowed for section “Other”. Returns true if the key was blocked.
 */
export function blockInvalidSectionOtherKey(event: KeyboardEvent): boolean {
    const keyLower = event.key.toLowerCase()
    const isCtrlA =
        keyLower === EI_INPUT_FIELD_CONSTANTS.KEYBOARD_SHORTCUTS.SELECT_ALL &&
        (event.ctrlKey || event.metaKey)
    const isCtrlC =
        keyLower === EI_INPUT_FIELD_CONSTANTS.KEYBOARD_SHORTCUTS.COPY &&
        (event.ctrlKey || event.metaKey)
    const isCtrlV =
        keyLower === EI_INPUT_FIELD_CONSTANTS.KEYBOARD_SHORTCUTS.PASTE &&
        (event.ctrlKey || event.metaKey)
    const isCtrlX =
        keyLower === EI_INPUT_FIELD_CONSTANTS.KEYBOARD_SHORTCUTS.CUT &&
        (event.ctrlKey || event.metaKey)
    const isCtrlZ =
        keyLower === EI_INPUT_FIELD_CONSTANTS.KEYBOARD_SHORTCUTS.UNDO &&
        (event.ctrlKey || event.metaKey)

    if (
        SECTION_OTHER_ALLOWED_KEYS.includes(
            event.key as (typeof SECTION_OTHER_ALLOWED_KEYS)[number],
        ) ||
        isCtrlA ||
        isCtrlC ||
        isCtrlV ||
        isCtrlX ||
        isCtrlZ
    ) {
        return false
    }

    if (event.key.length !== 1) {
        return false
    }

    if (!REGEX_PATTERNS.SECTION_OTHER_ALLOWED_SINGLE_KEY.test(event.key)) {
        event.preventDefault()
        return true
    }
    return false
}

export function exceedsMaxLength(value: string, maxLength: number): boolean {
    return value.length > maxLength
}

export function wouldExceedMaxLengthAfterInsert(
    currentValue: string,
    selectionStart: number,
    selectionEnd: number,
    insertLength: number,
    maxLength: number,
): boolean {
    const nextLength = currentValue.length - (selectionEnd - selectionStart) + insertLength
    return nextLength > maxLength
}

export function getFieldMaxLengthErrorMessage(fieldLabel: string, maxLength: number): string {
    return USER_FIELD_VALIDATION.messages.maxLength(fieldLabel, maxLength)
}

export function getConfiguredMaxLengthError(
    value: TInputFieldValue,
    config: Pick<IInputFieldConfig, 'label' | 'maxLength'>,
    messageFactory: (max: number, fieldLabel: string) => string,
): string | null {
    if (config.maxLength == null) {
        return null
    }
    const text = inputFieldValueToString(value)
    if (exceedsMaxLength(text, config.maxLength)) {
        return messageFactory(config.maxLength, config.label)
    }
    return null
}

export function getDisplayTextMaxLengthError(
    displayText: string,
    maxLength: number,
    fieldLabel: string,
    messageFactory: (max: number, fieldLabel: string) => string,
): string | null {
    if (exceedsMaxLength(displayText.trim(), maxLength)) {
        return messageFactory(maxLength, fieldLabel)
    }
    return null
}

export function truncateToMaxLength(value: string, maxLength: number): string {
    return value.length > maxLength ? value.slice(0, maxLength) : value
}
